import { useState, useEffect, useCallback } from 'react'
import Cookie from 'js-cookie'

interface UseRedirectionBannerProps {
  predirection: boolean
  sourceDomain?: string
  targetDomain?: string
  language?: string
}

interface UseRedirectionBannerReturn {
  showBanner: boolean
  sourceDomain: string
  targetDomain: string
  language: string
  handleClose: () => void
}

export const useRedirectionBanner = ({
  predirection,
  sourceDomain = 'wingie.com',
  targetDomain = 'wingie.com',
  language = 'tr',
}: UseRedirectionBannerProps): UseRedirectionBannerReturn => {
  const [showBanner, setShowBanner] = useState(false)

  useEffect(() => {
    // Banner gösterilecek koşullar:
    // 1. ?predirection=true query parametresi var
    // 2. domain_preference cookie'si yok
    const domainPreference = Cookie.get('domain_preference')

    if (predirection && !domainPreference) {
      setShowBanner(true)

      // Analytics event - Banner gösterildi
      if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
          event: 'redirection_banner_displayed',
          source_domain: sourceDomain,
          target_domain: targetDomain,
          user_id: Cookie.get('userId') || 'anonymous',
          timestamp: new Date().toISOString(),
        })
      }
    }

    const handleBannerClosedEvent = () => {
      setShowBanner(false)
    }

    window.addEventListener('redirection_banner_closed', handleBannerClosedEvent)

    return () => {
      window.removeEventListener('redirection_banner_closed', handleBannerClosedEvent)
    }
  }, [predirection, sourceDomain, targetDomain])

  const handleClose = useCallback(() => {
    // Cookie domain belirleme
    let cookieDomain: string | undefined

    if (targetDomain) {
      if (targetDomain.includes('localhost')) {
        cookieDomain = undefined
      } else if (targetDomain.includes('wingie')) {
        // Dinamik olarak root domain'i bul (wingie.pk, wingie.com, wingie.co.uk vs)
        const match = targetDomain.match(/wingie\.[a-z.]+$/)
        cookieDomain = match ? '.' + match[0] : '.wingie.com'
      } else if (targetDomain.includes('enuygun')) {
        cookieDomain = '.enuygun.com'
      }
    }

    // Kullanıcı banner'ı kapattı - bu durumda mevcut domain'de kalıyor
    Cookie.set('domain_preference', targetDomain, {
      expires: 7, // 7 gün
      domain: cookieDomain,
      path: '/',
      sameSite: 'Lax',
    })

    // Diğer komponentleri haberdar et
    window.dispatchEvent(new Event('redirection_banner_closed'))
    setShowBanner(false)

    // Analytics event - Banner kapatıldı
    if (typeof window !== 'undefined' && window.dataLayer) {
      window.dataLayer.push({
        event: 'redirection_banner_closed',
        source_domain: sourceDomain,
        target_domain: targetDomain,
        user_id: Cookie.get('userId') || 'anonymous',
        timestamp: new Date().toISOString(),
      })
    }
  }, [sourceDomain, targetDomain])

  return {
    showBanner,
    sourceDomain,
    targetDomain,
    language,
    handleClose,
  }
}
